import {
  apply, createGame, endTurn, resign, startTurnFlip, undoTurn,
  type Action, type EngineEvent, type GameState, type MapId, type Persona, type RNG,
} from '@cc/shared';
import { create } from 'zustand';
import { play } from '../audio/sfx';

export type LocalMode = 'local' | 'ai';

export interface LocalConfig {
  mode: LocalMode;
  mapId: MapId;
  names: [string, string];
  difficulty?: Persona;
  humanOriginalIndex?: 0;
}

export interface SavedGame {
  version: 1;
  savedAt: number;
  mode: LocalMode;
  config: LocalConfig;
  state: GameState;
  humanIndex: 0 | 1 | null;
  ceremony: boolean;
}

interface GameStore {
  mode: LocalMode | null;
  config: LocalConfig | null;
  state: GameState | null;
  humanIndex: 0 | 1 | null;
  ceremony: boolean;
  handoff: boolean;
  aiBusy: boolean;
  lastEvents: EngineEvent[];
  error: string | null;
  start(config: LocalConfig, goldIndex?: 0 | 1): void;
  restore(mode: LocalMode): boolean;
  dismissCeremony(): void;
  acknowledgeHandoff(): void;
  flip(): void;
  act(action: Action): boolean;
  undo(): void;
  finishTurn(): void;
  resign(): void;
  rematch(): void;
  setAiBusy(value: boolean): void;
  abandon(): void;
}

declare global { interface Window { __CC_TEST_RNG__?: RNG } }

function random(): number {
  if (typeof window !== 'undefined' && window.__CC_TEST_RNG__) return window.__CC_TEST_RNG__();
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return (values[0] ?? 0) / 0x1_0000_0000;
}

export const saveKey = (mode: LocalMode) => mode === 'local' ? 'cc:save:local' : 'cc:save:ai';

function persist(value: Omit<SavedGame, 'version' | 'savedAt'>) {
  const save: SavedGame = { ...value, version: 1, savedAt: Date.now() };
  localStorage.setItem(saveKey(value.mode), JSON.stringify(save));
}

export function readSave(mode: LocalMode): SavedGame | null {
  try {
    const raw = localStorage.getItem(saveKey(mode));
    if (!raw) return null;
    const save = JSON.parse(raw) as SavedGame;
    if (save.version !== 1 || save.mode !== mode || !save.state || !save.config) throw new Error('Invalid save');
    return save;
  } catch {
    localStorage.removeItem(saveKey(mode));
    return null;
  }
}

function sfxFor(events: EngineEvent[]) {
  for (const event of events) {
    if (event.type === 'flipStarted') play('flip');
    if (event.type === 'placed') play('place');
    if (event.type === 'paid') play('pay');
    if (event.type === 'moved') play('move');
    if (event.type === 'captured') play('capture');
    if (event.type === 'turnStarted') play('turn');
    if (event.type === 'gameOver') play('win');
  }
}

function persistCurrent(store: GameStore, state: GameState, ceremony = store.ceremony) {
  if (!store.mode || !store.config) return;
  if (state.phase === 'gameOver') {
    localStorage.removeItem(saveKey(store.mode));
    return;
  }
  persist({ mode: store.mode, config: store.config, state, humanIndex: store.humanIndex, ceremony });
}

export const useGameStore = create<GameStore>((set, get) => ({
  mode: null,
  config: null,
  state: null,
  humanIndex: null,
  ceremony: false,
  handoff: false,
  aiBusy: false,
  lastEvents: [],
  error: null,
  start: (config, givenGoldIndex) => {
    const goldIndex = givenGoldIndex ?? (random() < 0.5 ? 0 : 1);
    const state = createGame(config.mapId, config.names, goldIndex);
    const humanIndex = config.mode === 'ai' ? (goldIndex === 0 ? 0 : 1) : null;
    set({ mode: config.mode, config, state, humanIndex, ceremony: true, handoff: false, aiBusy: false, lastEvents: [], error: null });
    persist({ mode: config.mode, config, state, humanIndex, ceremony: true });
  },
  restore: (mode) => {
    const save = readSave(mode);
    if (!save) return false;
    set({ mode, config: save.config, state: save.state, humanIndex: save.humanIndex, ceremony: save.ceremony, handoff: false, aiBusy: false, lastEvents: [], error: null });
    return true;
  },
  dismissCeremony: () => {
    set({ ceremony: false });
    const store = get();
    if (store.state) persistCurrent(store, store.state, false);
  },
  acknowledgeHandoff: () => set({ handoff: false }),
  flip: () => {
    const store = get();
    if (!store.state || store.state.phase !== 'awaitFlip') return;
    try {
      const transition = startTurnFlip(store.state, random);
      sfxFor(transition.events);
      set({ state: transition.state, lastEvents: transition.events, error: null });
      persistCurrent(store, transition.state);
    } catch (error) { set({ error: error instanceof Error ? error.message : 'Flip failed.' }); }
  },
  act: (action) => {
    const store = get();
    if (!store.state) return false;
    const result = apply(store.state, action);
    if ('error' in result) { set({ error: result.error.message }); return false; }
    sfxFor(result.events);
    set({ state: result.state, lastEvents: result.events, error: null });
    persistCurrent(store, result.state);
    return true;
  },
  undo: () => {
    const store = get();
    if (!store.state) return;
    try {
      const transition = undoTurn(store.state);
      set({ state: transition.state, lastEvents: transition.events, error: null });
      persistCurrent(store, transition.state);
    } catch (error) { set({ error: error instanceof Error ? error.message : 'Undo failed.' }); }
  },
  finishTurn: () => {
    const store = get();
    if (!store.state) return;
    try {
      const transition = endTurn(store.state);
      sfxFor(transition.events);
      set({ state: transition.state, lastEvents: transition.events, handoff: store.mode === 'local', error: null });
      persistCurrent(store, transition.state);
    } catch (error) { set({ error: error instanceof Error ? error.message : 'Turn could not end.' }); }
  },
  resign: () => {
    const store = get();
    if (!store.state) return;
    const by = store.mode === 'ai' && store.humanIndex !== null ? store.humanIndex : store.state.current;
    const transition = resign(store.state, by);
    sfxFor(transition.events);
    set({ state: transition.state, lastEvents: transition.events });
    persistCurrent(store, transition.state);
  },
  rematch: () => { const config = get().config; if (config) get().start(config); },
  setAiBusy: (aiBusy) => set({ aiBusy }),
  abandon: () => {
    const mode = get().mode;
    if (mode) localStorage.removeItem(saveKey(mode));
    set({ mode: null, config: null, state: null, humanIndex: null, ceremony: false, handoff: false, aiBusy: false, lastEvents: [], error: null });
  },
}));
